import React, { useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { Button } from '../../../components';
import { domiciliaryScheduleService } from '../../../services';
import { toast } from 'react-hot-toast';

// Colours are auto-assigned (no picker — keeps the form to the fields you kept).
const PALETTE = ['#2563eb', '#0bb77b', '#f4a11a', '#ef4444', '#8b5cf6', '#0ea5e9', '#db2777', '#14b8a6'];

// Create / edit a zone: name, travel area (postcode prefixes), and the staff who
// work there. Each carer shows the zones they already cover; Add toggles them in.
const CreateZoneModal = ({ open, zone, carers = [], zones = [], onClose, onSaved }) => {
  const editing = !!(zone && zone.id);
  const [name, setName] = useState(zone?.name || '');
  const [prefixText, setPrefixText] = useState((zone?.postcode_prefixes || []).join(', '));
  const [carerIds, setCarerIds] = useState(zone?.carer_ids || []);
  const [saving, setSaving] = useState(false);

  // carerId → ["North Zone", ...] from the loaded zones (membership).
  const zonesByCarer = useMemo(() => {
    const map = {};
    zones.forEach((z) => (z.carer_ids || []).forEach((id) => {
      (map[id] = map[id] || []).push(z.name);
    }));
    return map;
  }, [zones]);

  // A carer belongs to one zone only, so the picker only offers carers who are
  // free — i.e. NOT in any OTHER zone. Creating a zone → only unassigned carers;
  // editing → this zone's carers plus any unassigned ones.
  const otherZoneCarerIds = useMemo(() => {
    const set = new Set();
    zones.forEach((z) => {
      if (String(z.id) !== String(zone?.id)) (z.carer_ids || []).forEach((id) => set.add(Number(id)));
    });
    return set;
  }, [zones, zone]);
  const availableCarers = carers.filter((c) => !otherZoneCarerIds.has(c.id));

  const toggle = (id) => setCarerIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const save = async () => {
    if (!name.trim()) { toast.error('Zone name is required'); return; }
    const payload = {
      name: name.trim(),
      color: editing ? zone.color : PALETTE[zones.length % PALETTE.length],
      postcode_prefixes: prefixText.split(',').map((p) => p.trim().toUpperCase()).filter(Boolean),
      carer_ids: carerIds,
    };
    setSaving(true);
    try {
      const res = editing
        ? await domiciliaryScheduleService.updateZone(zone.id, payload)
        : await domiciliaryScheduleService.createZone(payload);
      if (res.success) { toast.success(editing ? 'Zone updated' : 'Zone created'); onSaved(); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error saving zone');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={editing ? 'Edit zone' : 'Create zone'}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="success" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create zone'}
          </Button>
        </div>
      }
    >
      <p className="-mt-2 mb-4 text-xs text-gray-400 dark:text-gray-500">Set the travel area and assign the carers who can work there.</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Zone name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. South Zone"
            className="h-11 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Travel area</label>
          <input value={prefixText} onChange={(e) => setPrefixText(e.target.value)} placeholder="e.g. M20, M21"
            className="h-11 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 px-3 text-sm focus:border-blue-400 focus:outline-none" />
        </div>
      </div>
      <p className="mt-2 rounded-lg bg-gray-50 dark:bg-[#20242c] px-3 py-2 text-[11px] text-gray-400 dark:text-gray-500">
        Add postcode prefixes separated by commas. The same zone name and travel area appear in Care Packages, Schedule and future service-user selections.
      </p>

      <div className="mt-4 rounded-xl border border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] px-3 py-2">
          <span className="text-[13px] font-semibold text-gray-700 dark:text-gray-200">Staff assigned to this zone</span>
          <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500">{carerIds.length} selected</span>
        </div>
        <div className="grid max-h-[40vh] grid-cols-1 gap-1 overflow-y-auto p-2 sm:grid-cols-2">
          {availableCarers.length === 0 && <div className="col-span-full py-6 text-center text-xs text-gray-400 dark:text-gray-500">No unassigned carers — everyone already belongs to a zone.</div>}
          {availableCarers.map((c) => {
            const sel = carerIds.includes(c.id);
            const memberOf = (zonesByCarer[c.id] || []).join(' · ');
            return (
              <div key={c.id} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-gray-50 dark:hover:bg-[#31373f]">
                <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-blue-50 dark:bg-blue-900/30 text-[11px] font-bold text-blue-700 dark:text-blue-300">{c.initials}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-gray-800 dark:text-gray-200">{c.name}</div>
                  <div className="truncate text-[10px] text-gray-400 dark:text-gray-500">{memberOf || 'No zone yet'}</div>
                </div>
                <button
                  onClick={() => toggle(c.id)}
                  className={`h-7 rounded-md px-3 text-[11px] font-bold transition ${
                    sel ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
                  }`}
                >
                  {sel ? 'Added' : 'Add'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default CreateZoneModal;
